import { useEffect, useState } from 'react';
import { Modal } from '@/components/common/AppModal';
import { Form, Alert, Button, Spinner, InputGroup } from 'react-bootstrap';
import { useCreatePaymentMutation } from '@/app/Wallet/api/walletApi';
import { errorMessage } from '@/utils/toast';
import NumberRenderer from '@/app/common/components/CellRenderers/NumberRenderer';
import numberHumanizer from '@/helpers/numberHumanizer';

type ChargeWalletModalProps = {
  show: boolean;
  onHide: () => void;
};

type PaymentErrorData = {
  msg?: string;
  message?: string;
};

const MIN_CHARGE_TOMAN = 500_000;
const AMOUNT_STEP = 10_000;
const presetAmounts = [500_000, 2_000_000, 5_000_000];

const persianDigitToEnglish: Record<string, string> = {
  '۰': '0',
  '۱': '1',
  '۲': '2',
  '۳': '3',
  '۴': '4',
  '۵': '5',
  '۶': '6',
  '۷': '7',
  '۸': '8',
  '۹': '9',
};

const toEnglishDigits = (value: string) =>
  value.replace(/[۰-۹]/g, ch => persianDigitToEnglish[ch] ?? ch);

const formatFaToman = (value: number) =>
  `${value.toLocaleString('fa-IR')} تومان`;

const roundToChargeStep = (value: number) =>
  Math.round(value / AMOUNT_STEP) * AMOUNT_STEP;

const toValidChargeAmount = (value: number) =>
  Math.max(MIN_CHARGE_TOMAN, roundToChargeStep(value));

const getChargeAmountError = (value: number): string | null => {
  if (!Number.isFinite(value) || value <= 0) return null;
  if (value < MIN_CHARGE_TOMAN) {
    return `حداقل مبلغ شارژ ${formatFaToman(MIN_CHARGE_TOMAN)} است.`;
  }
  if (value % AMOUNT_STEP !== 0) {
    return 'چهار رقم آخر مبلغ باید صفر باشد (مضرب ۱۰٬۰۰۰ تومان).';
  }
  return null;
};

const ChargeWalletModal = ({ show, onHide }: ChargeWalletModalProps) => {
  const [amount, setAmount] = useState('');
  const [hasInteracted, setHasInteracted] = useState(false);
  const [createPayment, { isLoading: isCreatingPayment }] =
    useCreatePaymentMutation();

  useEffect(() => {
    if (!show) {
      setAmount('');
      setHasInteracted(false);
    }
  }, [show]);

  const parsedAmount =
    amount.trim() === '' ? NaN : Number.parseInt(amount, 10);
  const displayAmount = amount.trim() === '' ? '' : numberHumanizer(amount);
  const isFinitePositive = Number.isFinite(parsedAmount) && parsedAmount > 0;
  const validationError = isFinitePositive
    ? getChargeAmountError(parsedAmount)
    : null;
  const isValidChargeAmount = isFinitePositive && validationError === null;
  const suggestedAmount =
    isFinitePositive && validationError
      ? toValidChargeAmount(parsedAmount)
      : null;
  const showValidationError = hasInteracted && Boolean(validationError);
  const isPayDisabled = !isValidChargeAmount || isCreatingPayment;

  const applySuggestedAmount = () => {
    if (suggestedAmount == null) return;
    setAmount(String(suggestedAmount));
    setHasInteracted(true);
  };

  const handleBlur = () => {
    if (!isFinitePositive) return;
    setHasInteracted(true);
  };

  const handlePayment = async () => {
    setHasInteracted(true);

    if (!isValidChargeAmount) {
      errorMessage(
        validationError ??
          `حداقل مبلغ شارژ ${formatFaToman(MIN_CHARGE_TOMAN)} است.`
      );
      return;
    }

    try {
      const result = await createPayment({ amount: parsedAmount }).unwrap();
      if (result?.data?.redirectUrl) {
        location.replace(result.data.redirectUrl);
      } else {
        errorMessage('خطا در دریافت لینک پرداخت. لطفا دوباره تلاش کنید.');
      }
    } catch (error: unknown) {
      const errorData =
        typeof error === 'object' && error !== null && 'data' in error
          ? (error as { data?: PaymentErrorData }).data
          : undefined;

      errorMessage(
        errorData?.msg ||
          errorData?.message ||
          'خطا در دریافت لینک پرداخت. لطفا دوباره تلاش کنید.'
      );
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-1">شارژ کیف پول</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning" className="mb-3 fs--1">
          توجه: پس از شارژ کیف پول امکان عودت وجه وجود نخواهد داشت.
        </Alert>

        <Form.Group controlId="walletChargeAmount">
          <Form.Label>مبلغ شارژ (تومان)</Form.Label>
          <InputGroup hasValidation>
            <Form.Control
              type="text"
              inputMode="numeric"
              className="text-end"
              value={displayAmount}
              onBlur={handleBlur}
              onChange={e => {
                const digitsOnly = toEnglishDigits(
                  String(e.target.value)
                ).replace(/\D/g, '');
                setAmount(digitsOnly);
              }}
              placeholder="مثال: ۵۰۰٬۰۰۰"
              isInvalid={showValidationError}
              aria-describedby="walletChargeAmountHelp"
            />
            <InputGroup.Text className="fs--1">تومان</InputGroup.Text>
          </InputGroup>

          {showValidationError ? (
            <div className="invalid-feedback d-block" role="alert">
              {validationError}
              {suggestedAmount != null &&
              suggestedAmount !== parsedAmount ? (
                <Button
                  variant="link"
                  size="sm"
                  className="d-block px-0 mt-1 text-decoration-none"
                  onClick={applySuggestedAmount}
                >
                  استفاده از مبلغ پیشنهادی:{' '}
                  <NumberRenderer text={suggestedAmount} /> تومان
                </Button>
              ) : null}
            </div>
          ) : (
            <Form.Text
              id="walletChargeAmountHelp"
              className="d-block mt-1 fs--2 text-500"
            >
              حداقل {formatFaToman(MIN_CHARGE_TOMAN)} و مضرب ۱۰٬۰۰۰ تومان (چهار
              رقم آخر صفر).
            </Form.Text>
          )}
        </Form.Group>

        <div className="d-flex flex-wrap gap-2 mt-3">
          {presetAmounts.map(item => (
            <Button
              key={item}
              variant="outline-primary"
              size="sm"
              onClick={() => {
                setAmount(String(item));
                setHasInteracted(true);
              }}
            >
              <span className="d-inline-flex align-items-center gap-1">
                <NumberRenderer text={item} />
                <span>تومان</span>
              </span>
            </Button>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          disabled={isPayDisabled}
          onClick={handlePayment}
        >
          {isCreatingPayment ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              در حال انتقال به درگاه پرداخت...
            </>
          ) : (
            'پرداخت'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChargeWalletModal;
